import React from 'react';
import {InfoCircleOutlined} from '@ant-design/icons';
import {Card, Col, Row, Select, Spin, Tooltip, Typography} from 'antd';
import _ from 'lodash';

import {useNavigate} from 'react-router-dom';
import {useAppSelector} from '../../hooks/useStore';
import {selectCurrentProfile} from '../../store/slices/userSlice';
import {useHasJail} from '../../hooks/useGetJails';
import ErrorBoundary from '../ErrorBoundary';
import {useAssignPricePlanMutation, useGetPricePlansQuery} from '../../store/api/billingsApi';
import {useLazyGetProfileQuery} from '../../store/api/accountApi';
import {messageType, showMessages} from '../../helpers/message';
import {BaseError} from '../../types/apiTypes';

import './AccountDetails.less';

const {Text} = Typography;

const AccountDetailsCardStaff = () => {
    const navigate = useNavigate();
    const profile = useAppSelector(selectCurrentProfile);
    const account = profile?.account;

    const [isMigrated, isMigratedLoading] = useHasJail({profile, isStaff: true, jailName: 'data pushed to v7'});
    const isApplicant = profile?.roles?.includes('user.applicant') ?? false;
    const status = isMigrated ? 'Member on Engage 7' : isApplicant ? 'Applicant' : '';
    const pricePlan = account?.account_price_plan.name ? _.startCase(account?.account_price_plan.name) : 'Select one';

    const [getProfile] = useLazyGetProfileQuery();
    const {data: pricePlans, isFetching: fetchingPricePlans} = useGetPricePlansQuery();
    const [assignPricePlan, {isLoading: assignLoading, isSuccess: assignSuccess, isError: isAssignError, error: assignError, reset}] = useAssignPricePlanMutation();

    React.useEffect(() => {
        if (assignSuccess) {
            showMessages('Price plan assigned.', messageType.success);
            profile && getProfile(profile.id); // Replenish current profile.account for the updated price plan.
            reset();
        } else if (isAssignError) {
            showMessages((assignError as BaseError)?.data?.message, messageType.error);
            reset();
        }
    }, [assignError, getProfile, isAssignError, assignSuccess, profile, reset]);

    if (!account) {
        return navigate('/member_change');
    }

    return (
        <Spin spinning={isMigratedLoading || fetchingPricePlans || assignLoading}>
            <Card
                title={<><span>Account Details</span></>}
                className="details-card"
            >
                <ErrorBoundary>
                    <Row>
                        <Col span={24}>
                            <Text strong>Account Number:</Text>
                            <Tooltip title="This is your current account number">
                                <InfoCircleOutlined/>
                            </Tooltip>
                        </Col>
                        <Col span={24}>
                            <Text>{account.account_number ?? 'N/A'}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Text strong>Account Type:</Text>
                        </Col>
                        <Col span={24}>
                            <Text>{account.is_business ? 'Business' : 'Personal'}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Text strong>Legacy Account Number:</Text>
                            <Tooltip
                                title="Your legacy account number is your account number from the previous version of the software (this is only for reference)"
                            >
                                <InfoCircleOutlined/>
                            </Tooltip>
                        </Col>
                        <Col span={24}>
                            <Text copyable={!!account.legacy_account_number}>
                                {account.legacy_account_number ?? 'N/A'}
                            </Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Text strong>Price Plan:</Text>
                        </Col>
                        <Col>
                            <Select
                                className="price-plan-select"
                                loading={assignLoading}
                                dropdownMatchSelectWidth={false}
                                value={pricePlan}
                                options={pricePlans?.map(plan => ({
                                    value: plan.id,
                                    label: _.startCase(plan.name)
                                }))}
                                onChange={(planID) => {
                                    assignPricePlan({
                                        pricePlanID: Number(planID),
                                        accountID: account.id
                                    })
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Text strong>Account Owners:</Text>
                        </Col>
                        <Col span={24}>
                            {
                                account.account_owner_names && account.account_owner_names.length !== 0 ?
                                    account.account_owner_names.map((owner, index) => (
                                        <span className="account-owner-name long-text-wrap" key={index}>
                                            {owner ?? 'N/A'}
                                            {index !== account.account_owner_names.length - 1 ? ', ' : ''}
                                        </span>
                                    ))
                                    : 'N/A'
                            }
                        </Col>
                    </Row>
                    {!!status && (
                        <Row>
                            <Col span={24}>
                                <Text strong>Status:</Text>
                            </Col>
                            <Col span={24}>
                                <Text>{status}</Text>
                            </Col>
                        </Row>
                    )}
                </ErrorBoundary>
            </Card>
        </Spin>
    );
};

export default AccountDetailsCardStaff;
